import React, { useState, useEffect } from 'react';
import {
  X,
  Plus,
  Edit2,
  Trash2,
  Save,
  BarChart3,
  Search,
  Filter,
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  Target,
  Users
} from 'lucide-react';

import { authenticatedFetch } from '../services/auth';
import { API_BASE } from '../utils/apiConfig';


const IndicatorManagement = ({ onClose }) => {
  const [indicators, setIndicators] = useState([]);
  const [perspectives, setPerspectives] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingIndicator, setEditingIndicator] = useState(null);
  const [formData, setFormData] = useState({
    indicator_id: '',
    perspective_id: '',
    name: '',
    description: '',
    unit: '',
    target: '',
    higher_is_better: 'T',
    data_collection_date: '',
    responsible_user: '',
    objective_id: '',
    created_by: 'admin',
    primary_view_mode: 'CONSOLIDADO',
    bsc_frequency: 'MENSAL',
    tracking_criteria: '',
    area: '',
    source: '',
    responsible_name: '',
    responsible_phone: '',
    support_info: '',
    deadline: '',
    factors: '',
    formula: '',
    weight: 1
  });

  useEffect(() => {
    fetchIndicators();
    fetchPerspectives();
    fetchObjectives();
  }, []);

  const fetchIndicators = async () => {
    try {
      setLoading(true);
      console.log('Buscando indicadores...');

      const response = await authenticatedFetch(`${API_BASE}/api/bsc-indicators`);

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Indicadores carregados:', data);
      setIndicators(data || []);
      setError(null);
    } catch (err) {
      console.error('Erro ao carregar indicadores:', err);
      setError('Erro ao carregar indicadores: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPerspectives = async () => {
    try {
      console.log('Buscando perspectivas...');

      const response = await authenticatedFetch(`${API_BASE}/api/perspectives`);

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Perspectivas carregadas:', data);
      setPerspectives(data || []);
    } catch (err) {
      console.error('Erro ao carregar perspectivas:', err);
      setError('Erro ao carregar perspectivas: ' + err.message);
    }
  };

  const fetchObjectives = async () => {
    try {
      console.log('Buscando objetivos...');

      const response = await authenticatedFetch(`${API_BASE}/api/bsc-objectives`);

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Objetivos carregados:', data);
      setObjectives(data || []);
    } catch (err) {
      console.error('Erro ao carregar objetivos:', err);
      setError('Erro ao carregar objetivos: ' + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = editingIndicator
        ? `${API_BASE}/api/bsc-indicators/${editingIndicator.indicator_id}`
        : `${API_BASE}/api/bsc-indicators`;

      const method = editingIndicator ? 'PUT' : 'POST';

      const payload = editingIndicator
        ? {
          description: formData.description,
          target: formData.target,
          data_collection_date: formData.data_collection_date,
          responsible_user: formData.responsible_user,
          is_active: formData.is_active !== false,
          bsc_frequency: formData.bsc_frequency,
          tracking_criteria: formData.tracking_criteria,
          area: formData.area,
          source: formData.source,
          responsible_name: formData.responsible_name,
          responsible_phone: formData.responsible_phone,
          support_info: formData.support_info,
          deadline: formData.deadline || '',
          factors: formData.factors,
          formula: formData.formula,
          weight: parseFloat(formData.weight) || 0
        }
        : { ...formData, weight: parseFloat(formData.weight) || 0 };

      const response = await authenticatedFetch(url, {
        method,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      await fetchIndicators();
      setShowModal(false);
      setEditingIndicator(null);
      setFormData({
        indicator_id: '',
        perspective_id: '',
        name: '',
        description: '',
        unit: '',
        target: '',
        higher_is_better: 'T',
        data_collection_date: '',
        responsible_user: '',
        objective_id: '',
        created_by: 'admin',
        primary_view_mode: 'CONSOLIDADO',
        bsc_frequency: 'MENSAL',
        tracking_criteria: '',
        area: '',
        source: '',
        responsible_name: '',
        responsible_phone: '',
        support_info: '',
        deadline: '',
        factors: '',
        formula: '',
        weight: 1
      });
    } catch (err) {
      console.error('Erro ao salvar indicador:', err);
      setError('Erro ao salvar indicador: ' + err.message);
    }
  };

  const handleEdit = (indicator) => {
    setEditingIndicator(indicator);
    setFormData({
      indicator_id: indicator.indicator_id,
      perspective_id: indicator.perspective_id,
      name: indicator.name,
      description: indicator.description || '',
      unit: indicator.unit || '',
      target: indicator.target || '',
      higher_is_better: indicator.higher_is_better || 'T',
      data_collection_date: indicator.data_collection_date || '',
      responsible_user: indicator.responsible_user || '',
      objective_id: indicator.objective_id || '',
      is_active: indicator.is_active,
      created_by: indicator.created_by,
      primary_view_mode: indicator.primary_view_mode || 'CONSOLIDADO',
      bsc_frequency: indicator.bsc_frequency || 'MENSAL',
      tracking_criteria: indicator.tracking_criteria || '',
      area: indicator.area || '',
      source: indicator.source || '',
      responsible_name: indicator.responsible_name || '',
      responsible_phone: indicator.responsible_phone || '',
      support_info: indicator.support_info || '',
      deadline: indicator.deadline || '',
      factors: indicator.factors || '',
      formula: indicator.formula || '',
      weight: indicator.weight ?? 1
    });
    setShowModal(true);
  };

  const handleDelete = async (indicatorId) => {
    if (!window.confirm('Tem certeza que deseja desativar este indicador?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(
        `${API_BASE}/api/bsc-indicators/${indicatorId}`,
        {
          method: 'DELETE',
        },
      );

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      await fetchIndicators();
    } catch (err) {
      console.error('Erro ao deletar indicador:', err);
      setError('Erro ao deletar indicador: ' + err.message);
    }
  };

  const filteredIndicators = indicators.filter(ind => {
    const matchesSearch = ind.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ind.description && ind.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      ind.indicator_id.toString().includes(searchTerm.toLowerCase());

    const matchesFilter = filterActive === 'all' ||
      (filterActive === 'active' && ind.is_active) ||
      (filterActive === 'inactive' && !ind.is_active);

    return matchesSearch && matchesFilter;
  });

  const getPerspectiveName = (perspectiveId) => {
    const perspective = perspectives.find(p => p.perspective_id === perspectiveId);
    return perspective ? perspective.name : `ID: ${perspectiveId}`;
  };

  const getObjectiveName = (objectiveId) => {
    const objective = objectives.find(o => o.objective_id === parseInt(objectiveId));
    return objective ? objective.name : `ID: ${objectiveId}`;
  };

  const getFilteredObjectives = () => {
    if (!formData.perspective_id) return [];
    return objectives.filter(obj => obj.perspective_id === parseInt(formData.perspective_id));
  };

  return (





    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-4 lg:px-8 py-6">
          <div className="flex items-center justify-between">


            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gestão de Indicadores</h1>
                <p className="text-gray-600 text-sm">Gerenciar indicadores BSC</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="w-full mx-auto p-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {/* Filtros */}
        <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar indicadores..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setFilterActive('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterActive === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                Todos
              </button>
              <button
                onClick={() => setFilterActive('active')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterActive === 'active' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                Ativos
              </button>
              <button
                onClick={() => setFilterActive('inactive')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterActive === 'inactive' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                Inativos
              </button>
              <div className="ml-4 border-l border-gray-300 pl-4">
                <button
                  onClick={() => {
                    setEditingIndicator(null);
                    setFormData({
                      indicator_id: '',
                      perspective_id: '',
                      name: '',
                      description: '',
                      unit: '',
                      target: '',
                      higher_is_better: 'T',
                      data_collection_date: '',
                      responsible_user: '',
                      objective_id: '',
                      created_by: 'admin',
                      primary_view_mode: 'CONSOLIDADO',
                      bsc_frequency: 'MENSAL',
                      tracking_criteria: '',
                      area: '',
                      source: '',
                      responsible_name: '',
                      responsible_phone: '',
                      support_info: '',
                      deadline: '',
                      factors: '',
                      formula: '',
                      weight: 1
                    });
                    setShowModal(true);
                  }}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-white font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Novo Indicador
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de Indicadores */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Carregando indicadores...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Perspectiva
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Objetivo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unidade
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Meta
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Responsável
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredIndicators.map((indicator) => (
                    <tr key={indicator.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${indicator.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                          }`}>
                          {indicator.is_active ? (
                            <>
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Ativo
                            </>
                          ) : (
                            <>
                              <EyeOff className="w-3 h-3 mr-1" />
                              Inativo
                            </>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                        {indicator.indicator_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {indicator.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getPerspectiveName(indicator.perspective_id)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getObjectiveName(indicator.objective_id)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {indicator.unit || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {indicator.target || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {indicator.responsible_user || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(indicator)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(indicator.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredIndicators.length === 0 && (
                <div className="p-12 text-center">
                  <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Nenhum indicador encontrado</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Edição/Criação */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-8 w-full max-w-6xl mx-4 max-h-[95vh] overflow-y-auto shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingIndicator ? 'Editar Indicador' : 'Novo Indicador'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Seção 1: Informações Básicas */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Informações Básicas
                </h4>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Perspectiva *
                    </label>
                    <select
                      value={formData.perspective_id}
                      onChange={(e) => setFormData({ ...formData, perspective_id: parseInt(e.target.value), objective_id: '' })}
                      className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${editingIndicator ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      required
                      disabled={editingIndicator}
                    >
                      <option value="">Selecione uma perspectiva</option>
                      {perspectives.map((perspective) => (
                        <option key={perspective.perspective_id} value={perspective.perspective_id}>
                          {perspective.name}
                        </option>
                      ))}
                    </select>
                    {editingIndicator && (
                      <p className="text-xs text-gray-500 mt-1">Campo somente leitura</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Objetivo
                    </label>
                    <select
                      value={formData.objective_id}
                      onChange={(e) => setFormData({ ...formData, objective_id: e.target.value })}
                      className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${editingIndicator ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      disabled={!formData.perspective_id || editingIndicator}
                    >
                      <option value="">Selecione um objetivo</option>
                      {getFilteredObjectives().map((objective) => (
                        <option key={objective.objective_id} value={objective.objective_id}>
                          {objective.name}
                        </option>
                      ))}
                    </select>
                    {editingIndicator ? (
                      <p className="text-xs text-gray-500 mt-1">Campo somente leitura</p>
                    ) : !formData.perspective_id && (
                      <p className="text-xs text-gray-500 mt-1">Selecione uma perspectiva primeiro</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Maior é Melhor? *
                    </label>
                    <select
                      value={formData.higher_is_better}
                      onChange={(e) => setFormData({ ...formData, higher_is_better: e.target.value })}
                      className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${editingIndicator ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      disabled={editingIndicator}
                    >
                      <option value="T">Sim</option>
                      <option value="F">Não</option>
                    </select>
                    {editingIndicator && (
                      <p className="text-xs text-gray-500 mt-1">Campo somente leitura</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome do Indicador *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${editingIndicator ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      required
                      placeholder="Nome do indicador"
                      disabled={editingIndicator}
                    />
                    {editingIndicator && (
                      <p className="text-xs text-gray-500 mt-1">Campo somente leitura</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Critério de Acompanhamento
                    </label>
                    <input
                      type="text"
                      value={formData.tracking_criteria}
                      onChange={(e) => setFormData({ ...formData, tracking_criteria: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Critério para acompanhamento do indicador"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fatores
                    </label>
                    <textarea
                      value={formData.factors}
                      onChange={(e) => setFormData({ ...formData, factors: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      rows={3}
                      placeholder="Fatores que influenciam o indicador"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fórmula
                    </label>
                    <textarea
                      value={formData.formula}
                      onChange={(e) => setFormData({ ...formData, formula: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      rows={3}
                      placeholder="Fórmula de cálculo do indicador"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descrição
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    rows={3}
                    placeholder="Descrição detalhada do indicador"
                  />
                </div>
              </div>

              {/* Seção 2: Configurações de Meta e Coleta */}
              <div className="bg-blue-50 p-6 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-600" />
                  Configurações de Meta e Coleta
                </h4>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Unidade
                    </label>
                    <input
                      type="text"
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${editingIndicator ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      placeholder="%, R$, unid..."
                      disabled={editingIndicator}
                    />
                    {editingIndicator && (
                      <p className="text-xs text-gray-500 mt-1">Campo somente leitura</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meta
                    </label>
                    <input
                      type="text"
                      value={formData.target}
                      onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Valor da meta"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Peso (% no objetivo)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.weight}
                      onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Vale para os próximos períodos gerados. Soma dos pesos dos indicadores do objetivo = 100%.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Frequência BSC
                    </label>
                    <select
                      value={formData.bsc_frequency}
                      onChange={(e) => setFormData({ ...formData, bsc_frequency: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="DIARIA">Diária</option>
                      <option value="SEMANAL">Semanal</option>
                      <option value="MENSAL">Mensal</option>
                      <option value="TRIMESTRAL">Trimestral</option>
                      <option value="SEMESTRAL">Semestral</option>
                      <option value="ANUAL">Anual</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Modo de Visualização
                    </label>
                    <select
                      value={formData.primary_view_mode}
                      onChange={(e) => setFormData({ ...formData, primary_view_mode: e.target.value })}
                      className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${editingIndicator ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      disabled={editingIndicator}
                    >
                      <option value="CONSOLIDADO">Consolidado</option>
                      <option value="POR_NEGOCIO">Por Negócio</option>
                    </select>
                    {editingIndicator && (
                      <p className="text-xs text-gray-500 mt-1">Campo somente leitura</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data de Coleta
                    </label>
                    <input
                      type="text"
                      value={formData.data_collection_date}
                      onChange={(e) => setFormData({ ...formData, data_collection_date: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Ex: Mensal, Semanal..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fonte dos Dados
                    </label>
                    <input
                      type="text"
                      value={formData.source}
                      onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Origem/fonte do dado"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prazo (Dias após fechamento do mês)
                    </label>
                    <input
                      type="number"
                      value={formData.deadline}
                      onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Número de dias"
                      min="0"
                      max="31"
                    />
                  </div>
                </div>
              </div>

              {/* Seção 3: Responsabilidades e Organização */}
              <div className="bg-green-50 p-6 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-600" />
                  Responsabilidades e Organização
                </h4>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Responsável (Usuário)
                    </label>
                    <input
                      type="text"
                      value={formData.responsible_user}
                      onChange={(e) => setFormData({ ...formData, responsible_user: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Usuário responsável"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome do Responsável
                    </label>
                    <input
                      type="text"
                      value={formData.responsible_name}
                      onChange={(e) => setFormData({ ...formData, responsible_name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Nome completo do responsável"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone do Responsável
                    </label>
                    <input
                      type="text"
                      value={formData.responsible_phone}
                      onChange={(e) => setFormData({ ...formData, responsible_phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Área/Departamento
                    </label>
                    <input
                      type="text"
                      value={formData.area}
                      onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Departamento responsável"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Informações de Apoio
                    </label>
                    <textarea
                      value={formData.support_info}
                      onChange={(e) => setFormData({ ...formData, support_info: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      rows={3}
                      placeholder="Observações e informações adicionais"
                    />
                  </div>
                </div>
              </div>

              {editingIndicator && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                    Ativo
                  </label>
                </div>
              )}

              <div className="flex gap-4 pt-8 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 font-medium transition-colors shadow-lg"
                >
                  <Save className="w-5 h-5" />
                  {editingIndicator ? 'Atualizar Indicador' : 'Criar Indicador'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndicatorManagement;